import React, {useContext, useEffect, useState} from 'react';
import {PlayerContext} from "../../../../shared/contexts/playerContext";
import {internalApi} from "../../../../common/axios";
import {buildUrl} from "../../../../shared/utils/net";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {Button, Card, Descriptions, notification, Space, Table, Typography} from "antd";
import {AuthContext} from "../../../../shared/contexts/authContext";
import {useItemKrTextData} from "../../../probability/useItemData";
import IsLoading from "../../../../shared/components/IsLoading";
import ErrorPage from "../../../../shared/components/ErrorPage";

dayjs.extend(utc);

const {Title, Text} = Typography;

const Monthly = () => {
    const {playerData, setPlayerData} = useContext(PlayerContext);
    const {currentUser} = useContext(AuthContext);
    const [selectedMonthlyId, setSelectedMonthlyId] = useState(null);
    const [processedData, setProcessedData] = useState({monthlyDetails: {}, logsByMonthlyId: {}});
    const [syncing, setSyncing] = useState(false);
    const { data: krItemTextData, isLoading, error } = useItemKrTextData();

    useEffect(() => {
        fetchData();
    }, [playerData.userKey]);

    const processMonthlyData = (monthlySubscriptions, monthlyLogs) => {
        const monthlyDetails = {};
        const logsByMonthlyId = {};

        // 1. 월정액 구독 정보 가공
        monthlySubscriptions.forEach(sub => {
            monthlyDetails[sub.monthlyId] = {
                monthlyId: sub.monthlyId,
                begin: dayjs.unix(sub.begin).format('YYYY-MM-DD HH:mm:ss'),
                end: dayjs.unix(sub.end).format('YYYY-MM-DD HH:mm:ss'),
                endUnix: sub.end,
                isExtended: sub.isExtended === 1 ? '예' : '아니오',
                extendedCount: sub.extendedCount,
                lastRewardedAt: sub.lastRewardedAt ? dayjs.unix(sub.lastRewardedAt).format('YYYY-MM-DD HH:mm:ss') : 'N/A',
                userKey: sub.userKey,
            };
        });

        monthlyLogs.forEach(log => {
            let parsedItem = {};
            try {
                parsedItem = JSON.parse(log.item || '{}');
            } catch (e) {
                console.error("Failed to parse item JSON:", log.item, e);
            }

            if (Object.keys(parsedItem).length === 0) {
                return;
            }

            if (!logsByMonthlyId[log.monthlyId]) {
                logsByMonthlyId[log.monthlyId] = [];
            }
            logsByMonthlyId[log.monthlyId].push({
                ...log,
                created: dayjs.unix(log.created).format('YYYY-MM-DD HH:mm:ss'),
                item: parsedItem,
            });
        });

        return {monthlyDetails, logsByMonthlyId};
    };

    const fetchData = async () => {
        const result = await internalApi.get(buildUrl('/player/playInfo/monthly', {userKey: playerData.userKey}));
        const {
            monthlyDetails,
            logsByMonthlyId
        } = processMonthlyData(result.data.monthly || [], result.data.monthlyLog || []);
        setProcessedData({monthlyDetails, logsByMonthlyId});

        if (Object.keys(monthlyDetails).length > 0 && selectedMonthlyId === null) {
            setSelectedMonthlyId(parseInt(Object.keys(monthlyDetails)[0], 10));
        }
    }

    const handleSyncExpiryTime = async () => {
        setSyncing(true);
        try {
            const serverEndUtc = dayjs.unix(currentMonthlyDetail.endUnix).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
            const updatedPlayInfo = JSON.parse(JSON.stringify(playerData.playerInfo));
            updatedPlayInfo.shopData.ActiveMonthlyPayment[selectedMonthlyId].expiryTime = serverEndUtc;

            await internalApi.post(buildUrl('/player/playInfo'), {
                userKey: playerData.userKey,
                syncId: playerData.syncId,
                playInfo: updatedPlayInfo,
            });

            const refreshed = await internalApi.get(buildUrl('player', {
                type: 'userKey',
                value: playerData.userKey,
                target: currentUser.target,
            }));
            if (refreshed.data.player) {
                setPlayerData(refreshed.data.player);
            }

            notification.success({
                message: '동기화 완료',
                description: `종료일이 서버(${currentMonthlyDetail.end})와 동기화되었습니다.`,
                placement: 'bottomRight',
            });
        } catch (e) {
            notification.error({
                message: '동기화 실패',
                description: e.message,
                placement: 'bottomRight',
            });
        } finally {
            setSyncing(false);
        }
    };

    const handleMonthlySelect = (monthlyId) => {
        setSelectedMonthlyId(monthlyId);
        notification.info({
            message: `월정액 ${monthlyId} 선택`,
            description: `Monthly ID ${monthlyId}의 정보와 로그를 표시합니다.`,
            placement: 'bottomRight',
            duration: 1.5,
        });
    };

    if (isLoading) return <IsLoading />;
    if (error) return <ErrorPage error={error} />;

    const currentMonthlyDetail = selectedMonthlyId ? processedData.monthlyDetails[selectedMonthlyId] : null;
    const currentMonthlyLogs = selectedMonthlyId ? processedData.logsByMonthlyId[selectedMonthlyId] : [];
    const currentMonthlyPlayerData = selectedMonthlyId ? playerData.playerInfo?.shopData?.ActiveMonthlyPayment[selectedMonthlyId] : null;

    const logColumns = [
        {
            title: 'User Key',
            dataIndex: 'userKey',
            key: 'userKey',
        },
        {
            title: 'PID',
            dataIndex: 'pid',
            key: 'pid',
        },
        {
            title: '닉네임',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: '월정액 아이디',
            dataIndex: 'monthlyId',
            key: 'monthlyId',
        },
        {
            title: '아이템 (ID)',
            dataIndex: 'item',
            key: 'itemId',
            render: (item) => krItemTextData[currentUser.target].find(e => e.index === item.id).name + `(${item.id})`|| 'N/A',
        },
        {
            title: '수량',
            dataIndex: 'item',
            key: 'itemQuantity',
            render: (item) => item.quantity || 'N/A',
        },
        {
            title: '우편 발송일',
            dataIndex: 'created',
            key: 'created',
        },
    ];
    return (
        <div style={{padding: '20px'}}>
            <Title level={3}>월정액 정보 및 로그</Title>

            <Space wrap style={{marginBottom: '20px'}}>
                <Text strong>월정액 ID 선택:</Text>
                {Object.keys(processedData.monthlyDetails).map((monthlyId) => (
                    <Button
                        key={monthlyId}
                        type={selectedMonthlyId === parseInt(monthlyId, 10) ? 'primary' : 'default'}
                        onClick={() => handleMonthlySelect(parseInt(monthlyId, 10))}
                    >
                        Monthly ID {monthlyId}
                    </Button>
                ))}
                {Object.keys(processedData.monthlyDetails).length === 0 && (
                    <Text type="secondary">표시할 월정액이 없습니다.</Text>
                )}
            </Space>

            {selectedMonthlyId && currentMonthlyDetail ? (
                <>
                    <Card title={`월정액 ${selectedMonthlyId} 상세 정보`} style={{marginBottom: '20px'}}>
                        <Descriptions variant="outlined" column={1}>
                            <Descriptions.Item label="시작일">
                                <Text code>{currentMonthlyDetail.begin}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="종료일(서버)">
                                <Text code>{currentMonthlyDetail.end}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="종료일(클라이언트)">
                                <Space>
                                    <Text code>{dayjs(currentMonthlyPlayerData.expiryTime).format('YYYY-MM-DD HH:mm:ss')}</Text>
                                    <Button size="small" loading={syncing} onClick={handleSyncExpiryTime}>서버와 동기화</Button>
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="연장 횟수">
                                <Text strong>{currentMonthlyDetail.extendedCount}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="연장 여부">
                                <Text type={currentMonthlyDetail.isExtended === '예' ? 'success' : 'danger'}>
                                    {currentMonthlyDetail.isExtended}
                                </Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="최종 보상 수령일">
                                <Text code>{currentMonthlyDetail.lastRewardedAt}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Card title={`월정액 ${selectedMonthlyId} 관련 로그`} style={{marginBottom: '20px'}}>
                        <Table
                            columns={logColumns}
                            dataSource={currentMonthlyLogs}
                            rowKey="seq" // 각 로그의 고유 식별자로 'seq' 사용
                            pagination={{pageSize: 10}} // 페이지네이션 추가
                            scroll={{x: 'max-content'}} // 테이블 내용이 넘칠 경우 스크롤 허용
                            locale={{emptyText: "해당 월정액에 대한 로그가 없습니다."}}
                        />
                    </Card>
                </>
            ) : (
                <Card>
                    <Text type="secondary">월정액 ID를 선택해 주세요.</Text>
                </Card>
            )}
        </div>
    );
};

export default Monthly;